import { Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { MultiCommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { InHouseTableDiffChart } from "../../../components/UIToolkit/Graphs/TableDiffChart/InHouseTableDiffChart";
import { INK } from "../../../theme/tokens";
import type { ManagementCenterMultiDepartmentItem } from "../../../utils/models";
import { useManagementCenterDownload } from "../hooks/useManagementCenterDownload";
import { useManagementCenterMultiHppd } from "../hooks/useManagementCenterMulti";

interface IProps {
	maxRows: number;
	width: string;
	selectedRegions: string[];
	handleItemDoubleClick: (departmentName: string) => void;
	startDate: DateTime;
	endDate: DateTime;
}

export const HPPDComparison: FC<IProps> = ({ maxRows, width, selectedRegions, handleItemDoubleClick, startDate, endDate }) => {
	const { data, isLoading } = useManagementCenterMultiHppd({ startDate, endDate });
	const { download } = useManagementCenterDownload({ graphName: "hppd-comparison", startDate, endDate });
	const rawData = data?.items || [];
	const filteredData = selectedRegions.length === 0 ? rawData : rawData.filter((item) => selectedRegions.includes(item.region));

	const showWantedValues = (value: number) => {
		return <Typography sx={{ fontSize: 14, fontWeight: 500, color: alpha(INK.SECONDARY, 0.5) }}>/ {value}</Typography>;
	};

	const actualWrapper = (value: number) => {
		return `${value.toFixed(1)}`;
	};

	const items = filteredData.map((item: ManagementCenterMultiDepartmentItem) => ({
		...item,
		title: `${item.department}, ${item.region}`,
		percentageOfTarget: Math.round((item.actual / item.wanted) * 100),
		onDoubleClick: () => handleItemDoubleClick(item.department),
	}));

	const networkAvgActual = items.length > 0 ? Math.round(items.reduce((sum, item) => sum + item.actual, 0) / items.length) : undefined;

	const threshold = {
		warning: 9,
		error: 25,
	};

	return (
		<MultiCommonDashboardBox sx={{ display: "flex", flexDirection: "column", alignItems: "start", width }}>
			<InHouseTableDiffChart
				title="HPPD"
				subTitle={networkAvgActual !== undefined ? `Network Avg - ${networkAvgActual}` : "Network Avg - N/A"}
				items={items}
				isGoodFirst={true}
				threshold={threshold}
				closestToTargetIsBest={true}
				wantedWrapper={showWantedValues}
				maxVisibleItems={maxRows}
				isLoading={isLoading}
				onDownload={download}
				actualWrapper={actualWrapper}
			/>
		</MultiCommonDashboardBox>
	);
};
